// DataPanel — the app owner's window into their tenant DB: a collapsible tables
// rail, the rows table filling the pane, a floating pager, and inline editing.
// Rows are acted on by SELECTION: a sticky-left checkbox column (stays visible
// during horizontal scroll) selects rows; a floating action bar then offers
// Edit (one row) / Delete (any number) / Add. Warm-paper styling.
//
// Write model: values are edited as text and sent as-is — the DB casts them to
// the column type. An empty field means NULL on edit, and is OMITTED on insert
// (so the column default applies). Update/delete are keyed by the row's primary
// key; a table with no primary key is read-only (no safe way to target a row).

import { useRef, useState } from "react";
import { useAppData, PAGE_SIZE, type UseAppData } from "../hooks/useAppData";
import { lookupRow, type DbTable } from "../api";

const asStr = (v: unknown): string =>
  v === null || v === undefined ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);

export default function DataPanel({ slug }: { slug: string }) {
  const d = useAppData(slug);
  const [railOpen, setRailOpen] = useState(true);

  if (d.tablesError) return <Screen><ErrorNote text={d.tablesError} /></Screen>;
  if (d.tables === null) return <Screen><Muted>Loading tables…</Muted></Screen>;
  if (d.tables.length === 0) return <Screen><Muted>This app has no tables yet.</Muted></Screen>;

  const table = d.tables.find((t) => t.name === d.selected) ?? null;

  return (
    <div className="flex h-full w-full bg-bg">
      {railOpen ? (
        <aside className="flex w-52 shrink-0 flex-col border-r border-border">
          <div className="flex items-center justify-between px-3 pb-1.5 pt-3">
            <span className="pl-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-tertiary">Tables</span>
            <div className="flex items-center gap-0.5">
              <button type="button" onClick={d.reload} aria-label="Refresh" title="Refresh" className={RAIL_BTN}><RefreshIcon /></button>
              <button type="button" onClick={() => setRailOpen(false)} aria-label="Collapse tables" title="Collapse" className={RAIL_BTN}><ChevronLeft /></button>
            </div>
          </div>
          <nav className="flex-1 space-y-0.5 overflow-y-auto px-2.5 pb-3">
            {d.tables.map((t) => {
              const active = t.name === d.selected;
              return (
                <button key={t.name} type="button" onClick={() => d.select(t.name)} aria-pressed={active}
                  className={"w-full truncate rounded-lg px-3 py-2 text-left text-sm transition " +
                    (active ? "bg-accent-soft font-medium text-accent" : "text-text-secondary hover:bg-surface-muted hover:text-text")}>
                  {t.name}
                </button>
              );
            })}
          </nav>
        </aside>
      ) : (
        <div className="flex w-10 shrink-0 flex-col items-center gap-1 border-r border-border pt-3">
          <button type="button" onClick={() => setRailOpen(true)} aria-label="Show tables" title="Show tables" className={RAIL_BTN}><PanelIcon /></button>
          <button type="button" onClick={d.reload} aria-label="Refresh" title="Refresh" className={RAIL_BTN}><RefreshIcon /></button>
        </div>
      )}

      <section className="relative min-w-0 flex-1">
        <div className="absolute inset-0 overflow-auto">
          {d.rowsError
            ? <Screen><ErrorNote text={d.rowsError} /></Screen>
            : table && <RowsTable key={table.name} slug={slug} table={table} rows={d.page?.rows ?? null} loading={d.rowsLoading} d={d} />}
        </div>

        {table && d.page && !d.rowsError && (
          <FloatingPager total={d.page.total} offset={d.offset} count={d.page.rows.length}
            onPrev={() => d.setOffset(Math.max(0, d.offset - PAGE_SIZE))}
            onNext={() => d.setOffset(d.offset + PAGE_SIZE)} />
        )}
      </section>
    </div>
  );
}

// The editable grid. A sticky-left checkbox column selects rows; the floating
// action bar acts on the selection. Keyed by table name in the parent, so all
// this local state resets when the table changes.
function RowsTable({ slug, table, rows, loading, d }: { slug: string; table: DbTable; rows: Record<string, unknown>[] | null; loading: boolean; d: UseAppData }) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editId, setEditId]     = useState<string>("");   // JSON(pk) of the row in edit mode
  const [adding, setAdding]     = useState(false);
  const [draft, setDraft]       = useState<Record<string, string>>({});
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState<string | null>(null);

  const canEdit = table.primary_key.length > 0;   // need a key to target a row
  const keyOf = (row: Record<string, unknown>) => Object.fromEntries(table.primary_key.map((k) => [k, row[k]]));
  const idOf  = (row: Record<string, unknown>) => JSON.stringify(keyOf(row));
  const list  = rows ?? [];
  const byId  = new Map(list.map((r) => [idOf(r), r]));

  const reset = () => { setEditId(""); setAdding(false); setDraft({}); setErr(null); setConfirmDel(false); };
  const clearSel = () => setSelected(new Set());

  const toggleRow = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOnPage = list.length > 0 && list.every((r) => selected.has(idOf(r)));
  const someOnPage = !allOnPage && list.some((r) => selected.has(idOf(r)));
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(list.map(idOf)));

  const startEdit = (row: Record<string, unknown>) => {
    clearSel(); setAdding(false); setConfirmDel(false); setErr(null);
    setEditId(idOf(row));
    setDraft(Object.fromEntries(table.columns.map((c) => [c.name, asStr(row[c.name])])));
  };
  const startAdd = () => {
    clearSel(); setEditId(""); setConfirmDel(false); setErr(null);
    setAdding(true);
    setDraft(Object.fromEntries(table.columns.map((c) => [c.name, ""])));
  };

  const saveEdit = async () => {
    const row = byId.get(editId); if (!row) { reset(); return; }
    const set: Record<string, unknown> = {};
    for (const c of table.columns) {
      if (table.primary_key.includes(c.name)) continue;   // key columns aren't edited
      const next = draft[c.name] ?? "";
      if (next !== asStr(row[c.name])) set[c.name] = next === "" ? null : next;
    }
    if (Object.keys(set).length === 0) { reset(); return; }
    await run(() => d.update(keyOf(row), set));
  };
  const saveAdd = async () => {
    const row: Record<string, unknown> = {};
    for (const c of table.columns) { const v = (draft[c.name] ?? "").trim(); if (v !== "") row[c.name] = v; }
    await run(() => d.insert(row));
  };
  const deleteSelected = async () => {
    await run(async () => { for (const id of selected) { const row = byId.get(id); if (row) await d.remove(keyOf(row)); } clearSel(); });
  };
  async function run(fn: () => Promise<void>) {
    setBusy(true); setErr(null);
    try { await fn(); reset(); }
    catch (e) { setErr(String((e as Error)?.message ?? e)); setConfirmDel(false); }
    finally { setBusy(false); }
  }

  const busyRows = rows === null || (loading && list.length === 0);
  const editingRow = editId !== "" ? byId.get(editId) : undefined;
  const lead = canEdit ? 1 : 0;          // thin sticky checkbox rail
  const nCols = table.columns.length + lead;

  return (
    <>
      {err && (
        <div className="sticky left-0 top-0 z-30 flex items-start justify-between gap-3 border-b border-danger-border bg-danger-soft px-4 py-2 text-xs text-danger">
          <span className="leading-relaxed">{err}</span>
          <button type="button" onClick={() => setErr(null)} className="shrink-0 rounded px-1 hover:bg-danger-soft" aria-label="Dismiss">✕</button>
        </div>
      )}
      <div className="min-w-0 overflow-x-auto">
      {/* UN TABLEAU DÉFILE DANS SA BOÎTE, il ne pousse pas la page.
          `w-full` ne borne rien : la largeur INTRINSÈQUE d'un tableau (celle que son
          contenu exige) l'emporte, donc il déborde de son parent et c'est la PAGE qui
          se met à défiler de côté — la panne responsive classique, et la pire, parce
          qu'elle casse tout l'écran et pas seulement le tableau.
          `min-w-0` sur la boîte : sans lui, un enfant de flex refuse de rétrécir sous
          sa largeur intrinsèque et le débordement remonte d'un cran. */}
      <table className="w-full border-collapse text-left">
        <thead className="sticky top-0 z-20 bg-surface-muted">
          <tr className="group/head">
            {canEdit && (
              // Zero-width sticky anchor: reserves no column, but pins the
              // floating select-all to the viewport's left on horizontal scroll.
              <th className="sticky left-0 z-30 w-0 p-0">
                <input type="checkbox" aria-label="Select all rows on this page"
                  className={"absolute left-1 top-1/2 h-3.5 w-3.5 -translate-y-1/2 accent-accent transition-opacity " +
                    (allOnPage || someOnPage ? "opacity-100" : "opacity-0 group-hover/head:opacity-100 focus:opacity-100")}
                  checked={allOnPage} ref={(el) => { if (el) el.indeterminate = someOnPage; }} onChange={toggleAll} disabled={list.length === 0} />
              </th>
            )}
            {table.columns.map((c) => (
              <th key={c.name} className="whitespace-nowrap border-b border-border bg-surface-muted px-4 py-3 align-bottom">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-text">{c.name}</span>
                  {table.primary_key.includes(c.name) && <span className="rounded bg-accent-soft px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-accent">key</span>}
                </div>
                <div className="mt-0.5 text-[10px] font-normal lowercase text-text-tertiary">{c.type}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Add-row draft */}
          {adding && (
            <tr>
              {canEdit && <td className="sticky left-0 z-10 w-0 p-0" />}
              {table.columns.map((c) => {
                const isPk = table.primary_key.includes(c.name);
                return (
                  <td key={c.name} className="border-b border-border bg-accent-soft/30 px-3 py-1.5 align-top">
                    {isPk
                      // Primary keys are auto-generated: leave them out of the
                      // insert so the column default (e.g. gen_random_uuid()) fills
                      // them — never typed by hand.
                      ? <span className="text-xs italic text-text-tertiary" title="auto-generated on save">auto</span>
                      : <CellInput value={draft[c.name] ?? ""} placeholder="default" onChange={(v) => setDraft((s) => ({ ...s, [c.name]: v }))} />}
                  </td>
                );
              })}
            </tr>
          )}

          {busyRows
            ? <tr><td colSpan={nCols} className="px-4 py-10 text-center text-sm text-text-secondary">Loading…</td></tr>
            : list.length === 0 && !adding
              ? <tr><td colSpan={nCols} className="px-4 py-10 text-center text-sm text-text-secondary">No rows.</td></tr>
              : list.map((row) => {
                  const rid = canEdit ? idOf(row) : "";
                  const editing = editingRow === row;
                  const sel = canEdit && selected.has(rid);
                  const rowBg = editing ? "bg-accent-soft/20" : sel ? "bg-accent-soft/15" : "hover:bg-surface-muted/50";
                  return (
                    <tr key={rid || Math.random()} className={"group transition-colors " + rowBg}>
                      {canEdit && (
                        // Zero-width sticky anchor for the floating checkbox — no
                        // reserved column, pinned left on horizontal scroll.
                        <td className="sticky left-0 z-10 w-0 p-0">
                          {!editing && (
                            <input type="checkbox" aria-label="Select row"
                              className={"absolute left-1 top-1/2 h-3.5 w-3.5 -translate-y-1/2 accent-accent transition-opacity " +
                                (sel ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100")}
                              checked={sel} onChange={() => toggleRow(rid)} />
                          )}
                        </td>
                      )}
                      {table.columns.map((c) => {
                        const isPk = table.primary_key.includes(c.name);
                        return (
                          <td key={c.name} className="border-b border-border px-4 py-2.5 align-top">
                            {editing && !isPk
                              ? <CellInput value={draft[c.name] ?? ""} placeholder="null" onChange={(v) => setDraft((s) => ({ ...s, [c.name]: v }))} />
                              : c.references && row[c.name] != null
                                ? <FkCell slug={slug} value={row[c.name]} references={c.references} />
                                : <Cell value={row[c.name]} muted={editing && isPk} />}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}

          {/* Add-row trigger */}
          {!busyRows && !adding && !editId && (
            <tr>
              <td colSpan={nCols} className="px-4 py-2">
                <button type="button" onClick={startAdd}
                  className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-border px-3 py-1 text-xs text-text-secondary transition hover:border-border-active hover:text-text">
                  <span className="text-sm leading-none">＋</span> Add row
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
      <div className="h-20" />

      {/* Floating action bar — contextual: add / edit / delete-confirm / selection. */}
      <ActionBar
        busy={busy}
        mode={adding ? "add" : editId ? "edit" : confirmDel ? "confirm" : selected.size > 0 ? "select" : "none"}
        count={selected.size}
        onSave={adding ? saveAdd : saveEdit}
        onCancel={reset}
        onEdit={() => { const id = [...selected][0]; const r = id ? byId.get(id) : undefined; if (r) startEdit(r); }}
        onAskDelete={() => setConfirmDel(true)}
        onConfirmDelete={deleteSelected}
        onCancelDelete={() => setConfirmDel(false)}
        onClear={clearSel}
      />
    </>
  );
}

function ActionBar({ busy, mode, count, onSave, onCancel, onEdit, onAskDelete, onConfirmDelete, onCancelDelete, onClear }: {
  busy: boolean; mode: "none" | "add" | "edit" | "confirm" | "select"; count: number;
  onSave: () => void; onCancel: () => void; onEdit: () => void;
  onAskDelete: () => void; onConfirmDelete: () => void; onCancelDelete: () => void; onClear: () => void;
}) {
  if (mode === "none") return null;
  const wrap = "absolute bottom-4 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-full border border-border bg-surface/95 py-1.5 pl-4 pr-2 text-xs shadow-[0_2px_12px_rgba(0,0,0,0.12)] backdrop-blur";
  const primary = "rounded-full bg-accent px-3.5 py-1 font-medium text-white transition hover:bg-accent-hover disabled:opacity-50";
  const danger  = "rounded-full bg-danger-strong px-3.5 py-1 font-medium text-white transition hover:bg-danger-hover disabled:opacity-50";
  const ghost   = "rounded-full px-2.5 py-1 text-text-secondary transition hover:text-text disabled:opacity-50";
  if (mode === "add" || mode === "edit") {
    return (
      <div className={wrap}>
        <span className="text-text-secondary">{mode === "add" ? "New row" : "Editing row"}</span>
        <button type="button" className={primary} onClick={onSave} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        <button type="button" className={ghost} onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    );
  }
  if (mode === "confirm") {
    return (
      <div className={wrap}>
        <span className="text-text-secondary">Delete {count} {count === 1 ? "row" : "rows"}?</span>
        <button type="button" className={danger} onClick={onConfirmDelete} disabled={busy}>{busy ? "Deleting…" : "Delete"}</button>
        <button type="button" className={ghost} onClick={onCancelDelete} disabled={busy}>Cancel</button>
      </div>
    );
  }
  // select
  return (
    <div className={wrap}>
      <span className="text-text-secondary">{count} selected</span>
      {count === 1 && <button type="button" className="rounded-full border border-border px-3 py-1 text-text-secondary transition hover:border-border-active hover:text-text" onClick={onEdit}>Edit</button>}
      <button type="button" className={danger} onClick={onAskDelete}>Delete</button>
      <button type="button" className={ghost} onClick={onClear}>Clear</button>
    </div>
  );
}

function CellInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  // Seamless: transparent, borderless — blends into the cell. The row's tint
  // (accent-soft for add/edit) is what signals the field is editable; a faint
  // focus wash marks the active one without a white box.
  return (
    <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      className="w-full min-w-[6rem] max-w-[22rem] rounded bg-transparent px-1 py-0.5 font-mono text-xs text-text outline-none placeholder:text-text-tertiary focus:bg-black/[0.04]" />
  );
}

function Cell({ value, muted }: { value: unknown; muted?: boolean }) {
  if (value === null || value === undefined) return <span className="text-xs italic text-text-tertiary">null</span>;
  const text = typeof value === "object" ? JSON.stringify(value) : String(value);
  return <span className={"block max-w-[22rem] truncate font-mono text-xs " + (muted ? "text-text-tertiary" : "text-text")} title={text}>{text}</span>;
}

// A foreign-key cell: the value reads as a link, and hovering previews the row
// it points at (fetched once, then cached). Honestly shows an orphan (no match)
// — which, with FKs enforced, shouldn't happen.
function FkCell({ slug, value, references }: { slug: string; value: unknown; references: { table: string; column: string } }) {
  const text = typeof value === "object" ? JSON.stringify(value) : String(value);
  const [open, setOpen] = useState(false);
  const [row, setRow] = useState<Record<string, unknown> | null | undefined>(undefined); // undefined=unloaded, null=no match
  const [err, setErr] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const load = () => {
    if (row !== undefined || err) return;   // fetch at most once per cell
    lookupRow(slug, references.table, references.column, text)
      .then((r) => setRow(r.rows[0] ?? null))
      .catch((e) => setErr(String((e as Error)?.message ?? e)));
  };
  const enter = () => { clearTimeout(timer.current); timer.current = setTimeout(() => { setOpen(true); load(); }, 250); };
  const leave = () => { clearTimeout(timer.current); setOpen(false); };

  return (
    <span className="relative inline-block" onMouseEnter={enter} onMouseLeave={leave}>
      <span className="block max-w-[22rem] cursor-default truncate font-mono text-xs text-accent underline decoration-dotted underline-offset-2" title={text}>{text}</span>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-72 rounded-xl border border-border bg-surface p-3 text-left shadow-[0_4px_16px_rgba(0,0,0,0.14)]">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">→ {references.table}</div>
          {err
            ? <p className="text-xs text-danger">{err}</p>
            : row === undefined
              ? <p className="text-xs text-text-secondary">Loading…</p>
              : row === null
                ? <p className="text-xs italic text-text-secondary">No matching {references.table} row (orphan).</p>
                : <dl className="space-y-1">
                    {Object.entries(row).map(([k, v]) => (
                      <div key={k} className="flex items-baseline gap-2">
                        <dt className="w-24 shrink-0 truncate text-[11px] text-text-tertiary" title={k}>{k}</dt>
                        <dd className="min-w-0 flex-1 truncate font-mono text-[11px] text-text" title={v === null ? "null" : String(v)}>
                          {v === null || v === undefined ? <span className="italic text-text-tertiary">null</span> : typeof v === "object" ? JSON.stringify(v) : String(v)}
                        </dd>
                      </div>
                    ))}
                  </dl>}
        </div>
      )}
    </span>
  );
}

function FloatingPager({ total, offset, count, onPrev, onNext }: {
  total: number; offset: number; count: number; onPrev: () => void; onNext: () => void;
}) {
  const from = total === 0 ? 0 : offset + 1;
  const to = offset + count;
  const iconBtn = "rounded-full p-1.5 text-text-secondary transition enabled:hover:bg-surface-muted enabled:hover:text-text disabled:opacity-30";
  return (
    <div className="absolute bottom-4 right-4 z-20 flex items-center gap-0.5 rounded-full border border-border bg-surface/95 py-1 pl-3 pr-1.5 shadow-[0_2px_10px_rgba(0,0,0,0.10)] backdrop-blur">
      <span className="mr-1 text-[11px] tabular-nums text-text-secondary">{from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}</span>
      <button type="button" className={iconBtn} onClick={onPrev} disabled={offset <= 0} aria-label="Previous page" title="Previous"><ChevronLeft /></button>
      <button type="button" className={iconBtn} onClick={onNext} disabled={to >= total} aria-label="Next page" title="Next"><ChevronRight /></button>
    </div>
  );
}

function Screen({ children }: { children: React.ReactNode }) {
  return <div className="flex h-full w-full items-center justify-center p-10 text-center">{children}</div>;
}
function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-sm text-text-secondary">{children}</span>;
}
function ErrorNote({ text }: { text: string }) {
  return (
    <div className="max-w-md">
      <p className="text-sm font-medium text-text">Couldn’t load data</p>
      <p className="mt-1.5 text-xs leading-relaxed text-text-secondary">{text}</p>
    </div>
  );
}

const RAIL_BTN = "rounded-md p-1.5 text-text-secondary transition hover:bg-surface-muted hover:text-text";

const SVG = (p: { children: React.ReactNode }) => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{p.children}</svg>
);
function ChevronLeft()  { return <SVG><polyline points="15 18 9 12 15 6" /></SVG>; }
function ChevronRight() { return <SVG><polyline points="9 18 15 12 9 6" /></SVG>; }
function RefreshIcon()  { return <SVG><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></SVG>; }
function PanelIcon()    { return <SVG><rect x="3" y="4" width="18" height="16" rx="2" /><line x1="9" y1="4" x2="9" y2="20" /></SVG>; }
